/**
 * STABILITY-CRITICAL: gera e grava o backup que antecede apagar os dados locais. "Salvo" só
 * vale depois de writable.close() (seletor) ou de confirmação explícita do usuário (download).
 * Antes de alterar, leia docs/llm/slices/sync-engine.md e alinhe com o responsável pelo sync.
 */
import { saveAs } from 'file-saver'
import type { Motivo } from './diagnostics'

export type BackupFile = {
  blob: Blob
  filename: string
  farmsCount: number
  sizeKb: number
}
export type ExtraFile = { path: string; content: string }

export type AbortReason = 'cancelled' | 'write_failed' | 'picker_failed'
export class BackupAbortedError extends Error {
  reason: AbortReason
  constructor(reason: AbortReason, message?: string) {
    super(message ?? reason)
    this.name = 'BackupAbortedError'
    this.reason = reason
  }
}

export function backupFilename(
  motivo: 'limpeza' | 'saida',
  d: Date = new Date(),
): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `progerar-backup-${motivo}-${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}.zip`
}

type PrepareDeps = {
  db: any
  extra: (o: { motivo: Motivo; db: any }) => Promise<ExtraFile[]>
  build: (o: {
    selectedEstacaoDeMonta: string | null
    extraFiles: ExtraFile[]
  }) => Promise<{ blob: Blob; farmsCount: number }>
}

export async function prepareBackup(opts: {
  motivo: 'limpeza' | 'saida'
  now?: Date
  deps?: Partial<PrepareDeps>
}): Promise<BackupFile> {
  const db = opts.deps?.db ?? (await import('../../database')).default
  const extra =
    opts.deps?.extra ??
    (async (o: { motivo: Motivo; db: any }) =>
      (await import('./diagnostics')).buildBackupExtraFiles(o))
  const build =
    opts.deps?.build ??
    (async (o: {
      selectedEstacaoDeMonta: string | null
      extraFiles: ExtraFile[]
    }) => (await import('../../syncWorker')).buildBackupBlob(o as any))
  const extraFiles = await extra({ motivo: opts.motivo, db })
  const { blob, farmsCount } = await build({
    selectedEstacaoDeMonta: null,
    extraFiles,
  })
  return {
    blob,
    filename: backupFilename(opts.motivo, opts.now),
    farmsCount,
    sizeKb: Math.round(blob.size / 1024),
  }
}

export const supportsFilePicker = (
  win: any = typeof window !== 'undefined' ? window : undefined,
): boolean => typeof win?.showSaveFilePicker === 'function'

/** O CHAMADOR deve invocar isto direto de um clique (ativação do usuário). */
export async function saveWithPicker(file: BackupFile, win: any = window) {
  let handle: any
  try {
    handle = await win.showSaveFilePicker({
      suggestedName: file.filename,
      types: [
        {
          description: 'Backup ProGERAR (.zip)',
          accept: { 'application/zip': ['.zip'] },
        },
      ],
    })
  } catch (e: any) {
    if (e && e.name === 'AbortError') throw new BackupAbortedError('cancelled')
    throw new BackupAbortedError('picker_failed', String(e?.message ?? e))
  }
  let writable: any
  try {
    writable = await handle.createWritable()
    await writable.write(file.blob)
    await writable.close()
  } catch (e: any) {
    try {
      await writable?.abort?.()
    } catch {
      /* ignora */
    }
    throw new BackupAbortedError('write_failed', String(e?.message ?? e))
  }
  return { confirmed: true as const, method: 'picker' as const }
}

/** Fallback (Firefox/Safari) e SAIR sem DLQ: não há como saber se salvou — a UI exige o checkbox. */
export function saveByDownload(
  file: BackupFile,
  saveAsFn: (b: Blob, n: string) => void = saveAs,
): void {
  saveAsFn(file.blob, file.filename)
}
